import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import type { DestinationIconName } from "~/components/layout/nav-icon";
import { destinationForPath } from "~/components/layout/nav-items";
import { statusVisibleLabel } from "~/components/ui/status";
import { useI18n } from "~/i18n";
import type { Translate } from "~/i18n";
import { useSession } from "~/lib/access/session-context";
import { useApi } from "~/lib/api/api-context";
import { ncmLanguage } from "~/lib/api/queries.ncm-search";
import { decodeStoredTitle } from "~/lib/domain/evidence-title";
import { formatNcmDescription } from "~/lib/domain/ncm-description";
import {
  type OperatorHit,
  type OperatorRecord,
  type OperatorTarget,
  catalogHitsForQuery,
  filterDestinations,
  launchHitForQuery,
  mergeOperatorHits,
} from "~/lib/domain/operator-search";
import { formatNcm } from "~/lib/domain/quick-lookup";
import { useDebouncedValue } from "~/lib/ui/use-debounced-value";

const RECORD_LIMIT = 4;
/** Record lookups wait for a pause in typing: six categories per keystroke flooded the API. */
export const RECORD_SEARCH_DEBOUNCE_MS = 220;

function recordId(item: Record<string, unknown>): string | null {
  if (typeof item.id === "string" && item.id) return item.id;
  if (typeof item.calculationId === "string" && item.calculationId) return item.calculationId;
  return null;
}

function recordLabel(item: Record<string, unknown>, fallback: string): string {
  for (const key of ["title", "name", "summary", "description", "version", "reference"] as const) {
    const value = item[key];
    if (typeof value === "string" && value) {
      // Stored evidence titles can be percent-encoded; a result an operator
      // cannot read is a result they cannot pick.
      return key === "title" ? decodeStoredTitle(value) : value;
    }
  }
  return fallback;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export type SearchCategoryKey =
  | "evidence"
  | "rules"
  | "sources"
  | "proposals"
  | "calculations"
  | "products";

/**
 * The fiscal context an operator needs to pick the right record without
 * opening it: NCM, route and rate for a rule; publisher, UF and review state
 * for a document; reference and state for a proposal.
 */
export function recordContext(
  category: SearchCategoryKey,
  item: Record<string, unknown>,
  t: Translate,
): string | undefined {
  const parts: Array<string | null> = [];
  if (category === "rules") {
    const ncm = text(item.ncm);
    const origin = text(item.originUf);
    const destination = text(item.destinationUf);
    parts.push(ncm ? `NCM ${formatNcm(ncm)}` : null);
    parts.push(text(item.cest) ? `CEST ${text(item.cest)}` : null);
    parts.push(origin || destination ? `${origin ?? "*"} → ${destination ?? "*"}` : null);
    parts.push(text(item.rateLabel) ?? text(item.primaryRate));
    parts.push(text(item.rulesetVersion));
  } else if (category === "evidence") {
    parts.push(text(item.publisher) ?? text(item.sourceName));
    parts.push(text(item.jurisdiction));
    const status = text(item.reviewStatus);
    parts.push(status ? statusVisibleLabel(t, status, "evidence") : null);
  } else if (category === "proposals") {
    parts.push(text(item.reference));
    const ncm = text(item.ncm);
    parts.push(ncm ? `NCM ${formatNcm(ncm)}` : null);
    const status = text(item.status);
    parts.push(status ? statusVisibleLabel(t, status, "proposal") : null);
  } else {
    for (const key of ["publisher", "sourceName", "jurisdiction", "ncm", "status"] as const) {
      const value = text(item[key]);
      if (value) {
        parts.push(value);
        break;
      }
    }
  }
  const joined = parts.filter(Boolean).join(" · ");
  return joined || undefined;
}

/** A short leaf ("Outros", "De compressão") reads only with its parent level in front. */
function productLabel(item: {
  description: { text: string };
  breadcrumbs: ReadonlyArray<{ description: { text: string } }>;
}): string {
  const own = formatNcmDescription(item.description.text);
  const parent = item.breadcrumbs.at(-1);
  if (own.length >= 32 || !parent) return own;
  return `${formatNcmDescription(parent.description.text).replace(/:$/, "")} › ${own}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function collectRecords(
  page: { items: readonly unknown[] } | undefined,
  category: SearchCategoryKey,
  group: string,
  t: Translate,
  toTarget: (id: string) => OperatorTarget,
  filterNeedle?: string,
): OperatorRecord[] {
  const records: OperatorRecord[] = [];
  const folded = filterNeedle?.toLocaleLowerCase();
  for (const raw of page?.items ?? []) {
    if (!isRecord(raw)) continue;
    const item = raw;
    const id = recordId(item);
    if (!id) continue;
    const label = recordLabel(item, id);
    const hint = recordContext(category, item, t);
    if (folded && !`${label} ${hint ?? ""}`.toLocaleLowerCase().includes(folded)) continue;
    const target = toTarget(id);
    records.push({
      id: `${group}:${id}`,
      group,
      label,
      hint,
      to: target.to,
      params: target.params,
      search: target.search,
      icon: destinationForPath(target.to)?.icon,
    });
  }
  return records;
}

export type SearchCategoryState = {
  key: SearchCategoryKey;
  group: string;
  /** idle: query too short; denied: outside the operator's permissions. */
  state: "idle" | "loading" | "ready" | "error" | "denied";
};

export type OperatorSearchState = {
  hits: OperatorHit[];
  categories: SearchCategoryState[];
  /** At least one permitted category is still answering. */
  searching: boolean;
  /** Categories that failed; their absence is not "no results". */
  failed: SearchCategoryState[];
  denied: SearchCategoryState[];
  retryFailed: () => void;
};

type SearchInput = {
  open: boolean;
  query: string;
  destinations: Array<{ group: string; label: string; to: string; icon?: DestinationIconName }>;
};

/** Hits only (historical API). */
export function useOperatorSearch(input: SearchInput): OperatorHit[] {
  return useOperatorSearchState(input).hits;
}

/**
 * Operator search with per-category feedback: which record groups are still
 * loading, which failed and which are outside the operator's permissions,
 * so a slow or broken category never reads as "nothing found".
 */
export function useOperatorSearchState(input: SearchInput): OperatorSearchState {
  const { t, locale } = useI18n();
  const { can } = useSession();
  const api = useApi();
  const needle = input.query.trim();
  // Destinations and catalog hits follow every keystroke; API record lookups use the settled term.
  const recordNeedle = useDebouncedValue(needle, RECORD_SEARCH_DEBOUNCE_MS);
  const settling = recordNeedle !== needle;
  const enabled = input.open && recordNeedle.length >= 2;
  const canNews = can("READ:TAX_NEWS:ALL");
  const canRules = can("READ:TAX_RULE:ALL");
  const canCalc = can("READ:TAX_CALCULATION:ALL");
  // Product → NCM: words only (a typed code is already a catalog hit).
  const productQuery = /\p{L}/u.test(recordNeedle) && recordNeedle.length >= 3;
  const lang = ncmLanguage(locale);
  const catalogNcm = catalogHitsForQuery(recordNeedle, locale).find(
    (hit) => hit.catalogScope === "ncm" || hit.catalogScope === "prefix",
  )?.search?.ncm;

  const [evidence, rules, sources, proposals, calculations, products] = useQueries({
    queries: [
      {
        queryKey: ["operator-search", "evidence", recordNeedle],
        queryFn: () => api.listEvidence({ q: recordNeedle, limit: RECORD_LIMIT }),
        enabled: enabled && canNews,
        staleTime: 15_000,
      },
      {
        queryKey: ["operator-search", "rules", recordNeedle, catalogNcm],
        queryFn: () =>
          api.listRules({
            limit: RECORD_LIMIT,
            ...(catalogNcm ? { ncm: catalogNcm } : { q: recordNeedle }),
          }),
        enabled: enabled && canRules,
        staleTime: 15_000,
      },
      {
        queryKey: ["operator-search", "sources"],
        queryFn: () => api.listSources(),
        enabled: enabled && canNews,
        staleTime: 60_000,
      },
      {
        queryKey: ["operator-search", "proposals", recordNeedle],
        queryFn: () => api.listProposals({ q: recordNeedle, limit: RECORD_LIMIT }),
        enabled: enabled && canRules,
        staleTime: 15_000,
      },
      {
        queryKey: ["operator-search", "calculations", recordNeedle],
        queryFn: () => api.listCalculations({ q: recordNeedle, limit: RECORD_LIMIT }),
        enabled: enabled && canCalc,
        staleTime: 15_000,
      },
      {
        queryKey: ["operator-search", "products", lang, recordNeedle],
        queryFn: () => api.searchNcm({ q: recordNeedle, lang, limit: 5 }),
        enabled: enabled && canCalc && productQuery,
        staleTime: 5 * 60_000,
      },
    ],
  });

  const queries = { evidence, rules, sources, proposals, calculations, products } as const;
  const allowed: Record<SearchCategoryKey, boolean> = {
    evidence: canNews,
    rules: canRules,
    sources: canNews,
    proposals: canRules,
    calculations: canCalc,
    products: canCalc,
  };
  const categories: SearchCategoryState[] = (
    ["evidence", "rules", "sources", "proposals", "calculations", "products"] as const
  ).map((key) => {
    const group = t(`commandSearch.group.${key}`);
    const typedProduct = /\p{L}/u.test(needle) && needle.length >= 3;
    if (settling && input.open && needle.length >= 2 && allowed[key]) {
      if (key === "products" && !typedProduct) return { key, group, state: "idle" };
      return { key, group, state: "loading" };
    }
    if (!enabled || (key === "products" && !productQuery)) return { key, group, state: "idle" };
    if (!allowed[key]) return { key, group, state: "denied" };
    const query = queries[key];
    if (query.isError && !query.data) return { key, group, state: "error" };
    if (query.data) return { key, group, state: "ready" };
    return { key, group, state: "loading" };
  });
  const categorySignature = categories.map((category) => category.state).join("|");

  const hits = useMemo(() => {
    const destinations = filterDestinations(input.destinations, input.query);
    const launch = launchHitForQuery(needle, {
      group: t("commandSearch.group.launch"),
      label: t("commandSearch.launch.label", { query: needle || "—" }),
      hint: t("commandSearch.launch.hint"),
    });
    const catalog = catalogHitsForQuery(needle, locale).map((hit) => {
      if (hit.catalogScope === "chapter") {
        return {
          ...hit,
          group: t("commandSearch.group.catalog"),
          label: t("commandSearch.catalog.chapter.label", {
            chapter: hit.chapter ?? hit.label,
            title: hit.hint ?? "",
          }),
          hint: t("commandSearch.catalog.chapter.hint"),
        };
      }
      if (hit.catalogScope === "prefix") {
        return {
          ...hit,
          group: t("commandSearch.group.catalog"),
          label: t("commandSearch.catalog.prefix.label", { ncm: hit.search?.ncm ?? hit.label }),
          hint: t("commandSearch.catalog.hint"),
        };
      }
      return {
        ...hit,
        group: t("commandSearch.group.catalog"),
        label: t("commandSearch.catalog.label", { ncm: hit.search?.ncm ?? hit.label }),
        hint: t("commandSearch.catalog.hint"),
      };
    });

    const records = [
      ...collectRecords(evidence.data, "evidence", t("commandSearch.group.evidence"), t, (id) => ({
        to: "/evidence/$evidenceId",
        params: { evidenceId: id },
      })),
      ...collectRecords(rules.data, "rules", t("commandSearch.group.rules"), t, (id) => ({
        to: "/rules/$ruleId",
        params: { ruleId: id },
      })),
      ...collectRecords(
        sources.data,
        "sources",
        t("commandSearch.group.sources"),
        t,
        (id) => ({
          to: "/sources/$sourceId",
          params: { sourceId: id },
        }),
        needle,
      ),
      ...collectRecords(
        proposals.data,
        "proposals",
        t("commandSearch.group.proposals"),
        t,
        (id) => ({
          to: "/proposals/$proposalId",
          params: { proposalId: id },
        }),
      ),
      ...collectRecords(
        calculations.data,
        "calculations",
        t("commandSearch.group.calculations"),
        t,
        (id) => ({
          to: "/calculations/$calculationId",
          params: { calculationId: id },
        }),
      ),
    ];

    // Products → NCM sit with the catalog: what the operator typed, as official codes.
    const productHits: OperatorHit[] = (products.data?.items ?? []).map((item) => ({
      id: `product:${item.ncm}`,
      kind: "catalog" as const,
      group: t("commandSearch.group.products"),
      label: `${formatNcm(item.ncm)} · ${productLabel(item)}`,
      hint: [
        item.breadcrumbs.find((crumb) => crumb.level === "heading")?.description.text,
        t("commandSearch.product.hint"),
      ]
        .filter(Boolean)
        .map((part) => formatNcmDescription(part))
        .join(" · "),
      chapter: item.ncm.slice(0, 2),
      to: "/calculations/simulate",
      search: { mode: "individual", ncm: item.ncm },
    }));

    return mergeOperatorHits({
      launch,
      catalog: [...productHits, ...catalog],
      destinations,
      records,
    });
  }, [
    calculations.data,
    evidence.data,
    products.data,
    input.destinations,
    input.query,
    locale,
    needle,
    proposals.data,
    rules.data,
    sources.data,
    t,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the signature stands for the category states.
  const summary = useMemo(() => {
    const failed = categories.filter((category) => category.state === "error");
    return {
      searching: categories.some((category) => category.state === "loading"),
      failed,
      denied: categories.filter((category) => category.state === "denied"),
    };
  }, [categorySignature]);

  const retryFailed = () => {
    for (const category of summary.failed) void queries[category.key].refetch();
  };

  return { hits, categories, ...summary, retryFailed };
}
